import React, { useEffect, useState, useRef } from "react";
import { investorApi } from "../../api/api";
import { useSelector } from "react-redux";
import {
  IconButton,
  Modal,
  Box,
  CircularProgress,
  Typography,
  Paper
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  NavigateBefore,
  NavigateNext,
  PlayCircleOutline,
  Videocam
} from "@mui/icons-material";

const ProjectImages = ({ data }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [propertyVideos, setPropertyVideos] = useState([]);
  const [allMedia, setAllMedia] = useState([]); // Combined images and videos
  const [currentType, setCurrentType] = useState('image');
  
  const scrollRef = useRef(null);
  const modalScrollRef = useRef(null);
  const theme = useSelector((data) => data.themeValue.colors);

  const getYouTubeEmbedUrl = (url) => {
  if (!url) return "";

  const regExp =
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;

  const match = url.match(regExp);
  return match ? `https://www.youtube.com/embed/${match[1]}` : "";
};

  // Fetch images
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await investorApi.getProjectImagesById(data.id);
        if (response.data && Array.isArray(response.data)) {
          const imageUrls = response.data.map((img) => img.ImageURL);
          setImages(imageUrls);
          
          // Initialize with images
          const mediaWithType = imageUrls.map(url => ({
            type: 'image',
            url: url
          }));
          setAllMedia(mediaWithType);
        }
      } catch (err) {
        console.error("Image fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [data.id]);

  // Fetch videos
  useEffect(() => {
    const fetchVideos = async () => {
      if (!data.id) return;

      const setdata = {
        type: data.type || 2, // Default to type 2 if not specified
        reference_id: data.id,
      };

      try {
        const response = await investorApi.getPropertyVideos(setdata);

        if (response.data && Array.isArray(response.data.data)) {
          const videos = response.data.data.map(video => ({
            type: 'video',
            url: video.video_url,
            id: video.id
          }));
          setPropertyVideos(videos);
          
          // Add videos to the beginning of allMedia
          setAllMedia(prev => {
            const mediaWithType = videos.concat(prev);
            return mediaWithType;
          });
        }
      } catch (error) {
        console.error("Error fetching property videos:", error);
      }
    };
    fetchVideos();
  }, [data.id, data.type]);

  // Keyboard event handler
  useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          modalScrollLeft();
          break;
        case 'ArrowRight':
          modalScrollRight();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, currentIndex, allMedia.length]);

  const openModal = (index) => {
    setCurrentIndex(index);
    setCurrentType(allMedia[index]?.type || 'image');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const goNext = () => {
    if (currentIndex < allMedia.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setCurrentType(allMedia[currentIndex + 1]?.type || 'image');
    }
  };

  const goPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setCurrentType(allMedia[currentIndex - 1]?.type || 'image');
    }
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  const modalScrollLeft = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setCurrentType(allMedia[currentIndex - 1]?.type || 'image');
      modalScrollRef.current?.scrollBy({
        left: -modalScrollRef.current.offsetWidth,
        behavior: "smooth"
      });
    }
  };

  const modalScrollRight = () => {
    if (currentIndex < allMedia.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentType(allMedia[currentIndex + 1]?.type || 'image');
      modalScrollRef.current?.scrollBy({
        left: modalScrollRef.current.offsetWidth,
        behavior: "smooth"
      });
    }
  };

  // Scroll to current index when it changes
  useEffect(() => {
    if (modalOpen && modalScrollRef.current) {
      modalScrollRef.current.scrollTo({
        left: currentIndex * modalScrollRef.current.offsetWidth,
        behavior: "smooth"
      });
    }
  }, [currentIndex, modalOpen]);

  // Render media thumbnail
  const renderMediaThumbnail = (media, index) => {
    const isVideo = media.type === 'video';
    
    return (
      <Box
        key={index}
        sx={{
          flexShrink: 0,
          width: {
            xs: "70%",
            sm: "45%",
            md: "30%",
          },
          height: {
            xs: "12rem",
            sm: "14rem",
            md: "15rem",
          },
          borderRadius: 1,
          overflow: "hidden",
          cursor: "pointer",
          boxShadow: 1,
          position: "relative"
        }}
        onClick={() => openModal(index)}
      >
        {isVideo ? (
          <>
            <iframe
  src={getYouTubeEmbedUrl(media.url)}
  title="YouTube video"
  frameBorder="0"
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
  allowFullScreen
  style={{
    width: "100%",
    height: "100%",
    pointerEvents: "none", // so clicking opens modal
  }}
/>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.3)",
                transition: "background-color 0.3s",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.5)"
                }
              }}
            >
              <PlayCircleOutline 
                sx={{ 
                  fontSize: 50, 
                  color: "white" 
                }} 
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "white",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "2px"
              }}
            >
              <Videocam fontSize="small" />
              VIDEO
            </Box>
          </>
        ) : (
          <img
            src={media.url}
            alt={`property-${index}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      mt: 4,
      width: "100%",
      backgroundColor: theme.background,
      p: 2,
      borderRadius: 1
    }}>
      <Typography
        variant="h6"
        sx={{
          color: theme.text,
          mb: 2,
          ml: 1,
          display: "flex",
          alignItems: "center",
          gap: 1
        }}
      >
        Project Images
        {propertyVideos.length > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: "0.8rem",
              backgroundColor: "primary.main",
              color: "white",
              padding: "2px 8px",
              borderRadius: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            <Videocam fontSize="small" />
            {propertyVideos.length} video{propertyVideos.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Typography>
      <Paper elevation={3} sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
            <CircularProgress />
          </Box>
        ) : allMedia.length > 0 ? (
          <Box sx={{ position: "relative" }}>
            <IconButton
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                backgroundColor: "background.paper",
                boxShadow: 1,
                "&:hover": {
                  backgroundColor: "background.paper"
                }
              }}
              onClick={scrollLeft}
            >
              <ChevronLeft />
            </IconButton>
           
            <Box
              ref={scrollRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 2,
                px: 1,
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {allMedia.map((media, i) => renderMediaThumbnail(media, i))}
            </Box>
           
            <IconButton
              sx={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                backgroundColor: "background.paper",
                boxShadow: 1,
                "&:hover": {
                  backgroundColor: "background.paper"
                }
              }}
              onClick={scrollRight}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        ) : (
          <Typography>No media available</Typography>
        )}
      </Paper>

      {/* Media Modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: 'none'
        }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            bgcolor: "rgba(0, 0, 0, 0.95)",
            borderRadius: "10px",
            overflow: "hidden",
            position: "relative",
            outline: 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 3,
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.5)",
              '&:hover': {
                backgroundColor: "rgba(0,0,0,0.7)",
              }
            }}
            onClick={closeModal}
          >
            <Close />
          </IconButton>

          {/* Media counter */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 3,
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "#fff",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "14px"
            }}
          >
            {currentIndex + 1} / {allMedia.length}
            {allMedia[currentIndex]?.type === 'video' && (
              <Box component="span" sx={{ ml: 1, display: "flex", alignItems: "center", gap: "4px" }}>
                <Videocam fontSize="small" />
                VIDEO
              </Box>
            )}
          </Box>

          {currentIndex > 0 && (
            <IconButton
              sx={{
                position: "absolute",
                left: 20,
                top: "50%",
                zIndex: 3,
                backgroundColor: "rgba(255,255,255,0.8)",
                '&:hover': {
                  backgroundColor: "rgba(255,255,255,1)",
                }
              }}
              onClick={modalScrollLeft}
            >
              <NavigateBefore fontSize="large" />
            </IconButton>
          )}

          <Box
            ref={modalScrollRef}
            sx={{
              display: "flex",
              height: "100%",
              scrollSnapType: "x mandatory",
              overflowX: "auto",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": {
                display: "none"
              }
            }}
          >
            {allMedia.map((media, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  scrollSnapAlign: "center",
                  position: "relative"
                }}
              >
                {media.type === 'image' ? (
                  <img
                    src={media.url}
                    alt={`modal-img-${index}`}
                    style={{
                      maxWidth: "90%",
                      maxHeight: "90%",
                      objectFit: "contain"
                    }}
                  />
                ) : (
                  <iframe
  src={getYouTubeEmbedUrl(media.url)}
  title="YouTube video"
  frameBorder="0"
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
  allowFullScreen
  style={{
    width: "100%",
    height: "100%",
    // pointerEvents: "none", 
  }}
/>
                )}
              </Box>
            ))}
          </Box>

          {currentIndex < allMedia.length - 1 && (
            <IconButton
              sx={{
                position: "absolute",
                right: 20,
                top: "50%",
                zIndex: 3,
                backgroundColor: "rgba(255,255,255,0.8)",
                '&:hover': {
                  backgroundColor: "rgba(255,255,255,1)",
                }
              }}
              onClick={modalScrollRight}
            >
              <NavigateNext fontSize="large" />
            </IconButton>
          )}

          {/* Thumbnail navigation for mobile */}
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 1,
              zIndex: 3
            }}
          >
            {allMedia.map((_, index) => (
              <Box
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setCurrentType(allMedia[index]?.type || 'image');
                }}
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: index === currentIndex ? "#fff" : "rgba(255,255,255,0.3)",
                  cursor: "pointer",
                  transition: "background-color 0.3s"
                }}
              />
            ))}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProjectImages;